/**
 * EntityChip — a single canonical record rendered as a clickable chip.
 *
 * The atom shared by the Related tab (Surface 4) and Ask-Ken citation
 * links (Surface 3). It shows:
 *   - an icon chosen by `entity_type`,
 *   - the record `label`,
 *   - a 12px muted sub-label naming the entity type (de-snake-cased),
 *   - an optional status glyph (non-colour cue per §G.7).
 *
 * Linkability (ADR-0077): when the backend marks the item `linkable`
 * (status !== "not_linkable") AND `entityRoute()` resolves a route, the
 * chip renders a real `<a>` (stretched-link, a11y §G.3 — the whole chip
 * is the click target via an absolute-inset overlay anchor). Otherwise it
 * renders an inert `<span>` — never a dead link, never a button that goes
 * nowhere. The entity→route mapping is supplied via the `href` prop
 * (callers compute it with the shared `entityRoute` helper) so this
 * component stays router-agnostic; pass `LinkComponent` to use the host's
 * router, else a plain `<a>` is used.
 */

import {
  Users,
  Hammer,
  FileText,
  Receipt,
  CreditCard,
  Package,
  BookOpen,
  Factory,
  Briefcase,
  ClipboardList,
  Percent,
  FileSpreadsheet,
  Tag,
  Building2,
  NotebookPen,
  ShoppingCart,
  Box,
  type LucideIcon,
} from "lucide-react";
import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ComponentType,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
} from "react";

import { cn } from "../lib/utils";
import { StatusGlyph, type SemanticTone } from "./status-glyph";

/** Pascal `entity_type` → icon. Unknown types fall back to a neutral box. */
const ENTITY_ICONS: Readonly<Record<string, LucideIcon>> = {
  Customer: Users,
  CustomerJob: Hammer,
  Invoice: FileText,
  CreditMemo: FileText,
  SalesReceipt: Receipt,
  Payment: CreditCard,
  Product: Package,
  DiscountRule: Percent,
  ChartOfAccount: BookOpen,
  Supplier: Factory,
  Service: Briefcase,
  PurchaseOrder: ClipboardList,
  Receipt: Receipt,
  Bill: Receipt,
  BillPayment: CreditCard,
  VendorCredit: Receipt,
  TaxCode: Percent,
  Estimate: FileSpreadsheet,
  Class: Tag,
  Terms: Tag,
  FixedAsset: Building2,
  Expense: CreditCard,
  SalesOrder: ShoppingCart,
  JournalEntry: NotebookPen,
};

function iconFor(entityType: string): LucideIcon {
  return ENTITY_ICONS[entityType] ?? Box;
}

/** "CustomerJob" → "Customer Job"; "ChartOfAccount" → "Chart Of Account".
 *  De-snake-cases too, for unknown types that arrive snake_case. */
export function humanizeEntityType(entityType: string): string {
  if (entityType.includes("_")) {
    return entityType
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return entityType.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

export interface EntityChipProps {
  entityType: string;
  /** Display label for the record. */
  label: string;
  /** Resolved cockpit route, or null/undefined when not linkable. */
  href?: string | null;
  /** Optional semantic tone — drives the status glyph (non-colour cue). */
  tone?: SemanticTone;
  /** Router link component (e.g. TanStack Router `Link`). Falls back to <a>. */
  LinkComponent?: ComponentType<
    AnchorHTMLAttributes<HTMLAnchorElement> & { to?: string }
  >;
  /**
   * Optional click handler on the stretched anchor. The chip stays a real link
   * — `href` is untouched, so middle-click, "open in new tab" and keyboard
   * activation all still reach the document's page — but a handler that calls
   * `preventDefault()` can take over the plain click instead. This is what the
   * Related tab uses to open a document preview in place (owner QA caafcb60)
   * rather than navigating away. Ignored on an inert chip: a non-linkable chip
   * has nothing to activate.
   */
  onActivate?: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
  "data-testid"?: string;
  className?: string;
}

export const EntityChip = forwardRef<HTMLDivElement, EntityChipProps>(
  (
    {
      entityType,
      label,
      href,
      tone,
      LinkComponent,
      onActivate,
      "data-testid": testId,
      className,
    },
    ref,
  ): ReactElement => {
    const Icon = iconFor(entityType);
    const typeLabel = humanizeEntityType(entityType);
    const linkable = Boolean(href);

    const body = (
      <div
        ref={ref}
        data-testid={testId}
        data-linkable={linkable ? "true" : "false"}
        className={cn(
          "group relative inline-flex items-center gap-2 rounded-[var(--radius-md)]",
          "border border-[var(--color-border-hairline)] bg-[var(--color-surface)]",
          "pl-2.5 pr-3 py-1.5 max-w-full",
          linkable &&
            "transition-colors hover:bg-[var(--color-brand-soft)] hover:border-[var(--color-brand-soft)] focus-within:ring-2 focus-within:ring-[var(--color-brand)]",
          className,
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "grid place-items-center w-7 h-7 rounded-[var(--radius-sm)] shrink-0",
            "bg-[var(--color-surface-soft)] text-[var(--color-text-soft)]",
            linkable && "group-hover:text-[var(--color-brand-strong)]",
          )}
        >
          <Icon size={15} />
        </span>
        <span className="flex flex-col min-w-0">
          <span
            className={cn(
              "text-[13px] font-medium truncate text-[var(--color-text-strong)]",
              linkable && "group-hover:text-[var(--color-brand-strong)]",
            )}
          >
            {label}
            {tone ? (
              <span
                className="ml-1.5 inline-flex items-center align-middle text-[var(--color-text-faint)]"
              >
                <StatusGlyph tone={tone} size={11} />
              </span>
            ) : null}
          </span>
          <span className="text-[12px] text-[var(--color-text-faint)] truncate">
            {typeLabel}
          </span>
        </span>
      </div>
    );

    if (!linkable) {
      // Inert chip — label-only, no link, no hover affordance.
      return body;
    }

    // Stretched-link (§G.3): a single absolutely-positioned anchor covers
    // the whole chip, so the entire chip is one keyboard/pointer target
    // without nesting interactive elements.
    const stretchedClass =
      "absolute inset-0 rounded-[var(--radius-md)] focus:outline-none";
    const anchor = LinkComponent ? (
      <LinkComponent
        to={href!}
        href={href!}
        aria-label={`${label} — ${typeLabel}`}
        className={stretchedClass}
        onClick={onActivate}
      >
        <span className="sr-only">{`${label} — ${typeLabel}`}</span>
      </LinkComponent>
    ) : (
      <a
        href={href!}
        aria-label={`${label} — ${typeLabel}`}
        className={stretchedClass}
        onClick={onActivate}
      >
        <span className="sr-only">{`${label} — ${typeLabel}`}</span>
      </a>
    );

    return (
      <div className="relative inline-flex max-w-full">
        {body}
        {anchor}
      </div>
    );
  },
);
EntityChip.displayName = "EntityChip";
